import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 120000,
});

// ===== Job polling helper =====
// Lanza un job en background y hace polling cada 2.5s hasta done/error.
// Evita timeouts del ingress (~60-100s) en llamadas largas a Claude.
async function runJob(kind, payload, { interval = 2500, maxWaitMs = 5 * 60 * 1000 } = {}) {
  const { data } = await api.post("/jobs/run", { kind, payload });
  const jobId = data.job_id;
  const started = Date.now();
  // Polling
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - started > maxWaitMs) {
      throw new Error("La IA está tardando demasiado. Inténtalo de nuevo en un momento.");
    }
    await new Promise((r) => setTimeout(r, interval));
    let status;
    try {
      const res = await api.get(`/jobs/${jobId}`);
      status = res.data;
    } catch (e) {
      // Reintento silencioso ante errores transitorios de red
      continue;
    }
    if (status.status === "done") return status.result;
    if (status.status === "error") {
      const err = new Error(status.error || "La IA no pudo completar la petición");
      err.userMessage = status.error || "Error generando el resultado";
      throw err;
    }
  }
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const msg = err?.response?.data?.detail || err?.message || "Error inesperado";
    err.userMessage = msg;
    return Promise.reject(err);
  }
);

export async function generateOffer(payload) {
  return await runJob("offer", payload);
}

export async function analyzeClient(payload) {
  return await runJob("analyze", payload);
}

export async function generateProposal(payload) {
  return await runJob("proposal", payload);
}

export async function detectScope(payload) {
  return await runJob("scope", payload);
}

export async function checkPricing(payload) {
  return await runJob("pricing", payload);
}

export async function saveItem(payload) {
  const { data } = await api.post("/library/save", payload);
  return data;
}

export async function listItems(kind) {
  const { data } = await api.get(`/library/list${kind ? `?kind=${kind}` : ""}`);
  return data;
}

export async function libraryStats() {
  const { data } = await api.get("/library/stats");
  return data;
}

// ===== Auth =====
export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function exchangeSession(sessionId) {
  const { data } = await api.post("/auth/session", null, {
    headers: { "X-Session-ID": sessionId },
  });
  return data;
}

export async function logout() {
  const { data } = await api.post("/auth/logout");
  return data;
}

// ===== Billing =====
export async function listPlans() {
  const { data } = await api.get("/billing/plans");
  return data;
}

export async function createCheckout(plan) {
  const { data } = await api.post("/billing/checkout", {
    plan,
    origin_url: window.location.origin,
  });
  return data;
}

export async function getCheckoutStatus(sessionId) {
  const { data } = await api.get(`/billing/status/${sessionId}`);
  return data;
}

export async function openBillingPortal() {
  const { data } = await api.post("/billing/portal", {
    origin_url: window.location.origin,
  });
  return data;
}

export async function syncSubscription() {
  const { data } = await api.post("/billing/sync");
  return data;
}

// ===== Admin 2FA (TOTP) =====
export async function adminTotpStatus() {
  const { data } = await api.get("/admin/2fa/status");
  return data;
}

export async function adminTotpSetup() {
  const { data } = await api.post("/admin/2fa/setup");
  return data;
}

export async function adminTotpEnable(code) {
  const { data } = await api.post("/admin/2fa/enable", { code });
  return data;
}

export async function adminTotpVerify(code) {
  const { data } = await api.post("/admin/2fa/verify", { code });
  return data;
}

export async function adminTotpRevoke() {
  const { data } = await api.post("/admin/2fa/revoke");
  return data;
}

export async function adminStats() {
  const { data } = await api.get("/admin/stats");
  return data;
}

export async function adminDashboardOverview() {
  const { data } = await api.get("/admin/dashboard/overview");
  return data;
}

export async function adminDashboardTimeseries(range = "30d") {
  const { data } = await api.get(`/admin/dashboard/timeseries?range=${range}`);
  return data;
}

export async function adminDashboardUsage() {
  const { data } = await api.get("/admin/dashboard/usage");
  return data;
}

export async function adminDashboardBusinessHealth() {
  const { data } = await api.get("/admin/dashboard/business-health");
  return data;
}

export async function adminListUsers(params = {}) {
  const q = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/users${q ? `?${q}` : ""}`);
  return data;
}

export async function adminUpdateUser(userId, patch) {
  const { data } = await api.patch(`/admin/users/${encodeURIComponent(userId)}`, patch);
  return data;
}

export async function adminDeleteUser(userId) {
  const { data } = await api.delete(`/admin/users/${encodeURIComponent(userId)}`);
  return data;
}
